/**
 * Training Edit Page
 * Page for editing existing trainings
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PageShell } from '../../components';
import { trainingService } from '../../services/trainingService';
import { Training, TrainingFormData } from '../../types/training';
import { TrainingForm } from './components/TrainingForm';

const BREADCRUMBS = [
  { label: 'Início', path: '/' },
  { label: 'Treinamentos', path: '/trainings' },
  { label: 'Editar Treinamento' },
];

export const TrainingEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [training, setTraining] = useState<Training | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTraining = async () => {
      if (!id) return;

      try {
        setLoadingData(true);
        const data = await trainingService.getTrainingById(Number(id));
        setTraining(data);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar treinamento';
        setError(errorMessage);
      } finally {
        setLoadingData(false);
      }
    };

    loadTraining();
  }, [id]);

  const handleSubmit = async (data: TrainingFormData) => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      await trainingService.updateTraining(Number(id), data);
      navigate('/trainings');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar treinamento';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/trainings');
  };

  if (loadingData) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar Treinamento">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!training) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar Treinamento">
        <Alert severity="error">Treinamento não encontrado</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={BREADCRUMBS}
      title="Editar Treinamento"
      subtitle={`${training.name} - ${training.employeeName ?? ''}`}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TrainingForm
        initialData={training}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
      />
    </PageShell>
  );
};

export default TrainingEditPage;
